<template>
    <div>
        <slot name="title"></slot>

        <text-area-field
            v-model="complications"
            :field="finalEvaluationFields.complications"
            field-name="complications"
            :inline="false"
            rows="4"
            class="mb-3"
        />

        <select-as-radio-field
            v-model="exitus"
            :field="finalEvaluationFields.exitus"
            field-name="exitus"
            yes-no
            class="mb-3"
        />

        <div v-if="exitus" class="ml-3">
            <date-field
                ref="exitusDate"
                v-model="exitus_date"
                :field="finalEvaluationFields.exitus_date"
                :inline="false"
                field-name="exitus-date"
                class="mb-3"
            />

            <text-area-field
                v-model="exitus_reason"
                :field="finalEvaluationFields.exitus_reason"
                field-name="exitus-reason"
                :inline="false"
                class="mb-3"
            />
        </div>

        <date-field
            :value="surgery.hospitalization_date"
            :field="surgeryFields.hospitalization_date"
            field-name="hospitalization-date"
            :inline="false"
            disabled
            class="mb-3"
            datetime
        />

        <date-field
            ref="dischargePatientDate"
            v-model="discharge_patient_date"
            :field="finalEvaluationFields.discharge_patient_date"
            field-name="discharge-patient-date"
            :inline="false"
            class="mb-3"
            datetime
        />

        <select-as-radio-field
            v-model="inhibitors_3_months"
            :field="finalEvaluationFields.inhibitors_3_months"
            field-name="inhibitor-three-months"
            class="mb-3"
        />

        <select-as-radio-field
            v-model="iron"
            :field="finalEvaluationFields.iron"
            field-name="has-iron"
            yes-no
            class="mb-3"
        />

        <select-as-radio-field
            v-model="has_thrombosis"
            :field="finalEvaluationFields.has_thrombosis"
            field-name="has-thrombosis"
            class="mb-3"
            yes-no
        />

        <div v-if="has_thrombosis" class="ml-3">
            <date-field
                ref="thrombosisDate"
                v-model="thrombosis_date"
                :field="finalEvaluationFields.thrombosis_date"
                :inline="false"
                field-name="thrombosis-date"
                class="mb-3"
            />

            <text-area-field
                v-model="thrombosis_type"
                :field="finalEvaluationFields.thrombosis_type"
                :inline="false"
                field-name="thrombosis-type"
                rows="1"
                class="mb-3"
            />
        </div>

        <select-as-radio-field
            v-model="has_allergic_reaction"
            :field="finalEvaluationFields.has_allergic_reaction"
            field-name="has-allergic-reaction"
            yes-no
            class="mb-3"
        />

        <div v-if="has_allergic_reaction" class="ml-3">
            <date-field
                ref="allergicReactionDate"
                v-model="allergic_reaction_date"
                :field="finalEvaluationFields.allergic_reaction_date"
                :inline="false"
                field-name="allergic-reaction-date"
                class="mb-3"
            />

            <select-as-radio-field
                v-model="allergic_reaction_seriousness"
                :field="finalEvaluationFields.allergic_reaction_seriousness"
                field-name="allergic-reaction-seriousness"
                class="mb-3"
            />

            <text-area-field
                v-model="allergic_reaction_comments"
                :field="finalEvaluationFields.allergic_reaction_comments"
                :inline="false"
                field-name="allergic-reaction-comments"
                rows="1"
                class="mb-3"
            />
        </div>

        <select-as-radio-field
            v-model="has_adverse_reaction"
            :field="finalEvaluationFields.has_adverse_reaction"
            field-name="has-adverse-reaction"
            yes-no
            class="mb-3"
        />

        <div v-if="has_adverse_reaction" class="ml-3">
            <text-area-field
                v-model="adverse_reaction"
                :field="finalEvaluationFields.adverse_reaction"
                :inline="false"
                :errors="$v.adverse_reaction.$invalid ? {['adverse-reaction']: $t('error.required')} : {}"
                :lazy="false"
                field-name="adverse-reaction"
                rows="1"
                class="mb-3"
            />
        </div>

        <text-area-field
            v-model="hemorrhages"
            :field="finalEvaluationFields.hemorrhages"
            :inline="false"
            field-name="hemorrhages"
            rows="1"
            class="mb-3"
        />

    </div>
</template>

<script>
    import {mapState} from "vuex";
    import {mapStateProperty} from "@/utils/VuexHelpers";
    import TextAreaField from "@/components/fields/TextAreaField";
    import SelectAsRadioField from "@/components/fields/SelectAsRadioField";
    import DateField from "@/components/fields/DateField";
    import NavigableComponentMixin from "@/components/mixins/NavigableComponentMixin";
    import {requiredIf} from "vuelidate/lib/validators";

    export default {
        name: "FinalEvaluationData",
        components: {DateField, SelectAsRadioField, TextAreaField},
        mixins: [NavigableComponentMixin],
        computed: {
            ...mapState([
                'surgery',
                'surgeryFields',
                'finalEvaluationFields',
            ]),
            ...mapStateProperty([
                'finalEvaluation.complications',
                'finalEvaluation.exitus',
                'finalEvaluation.exitus_date',
                'finalEvaluation.exitus_reason',
                'finalEvaluation.discharge_patient_date',
                'finalEvaluation.inhibitors_3_months',
                'finalEvaluation.iron',
                'finalEvaluation.hemorrhages',
                'finalEvaluation.has_thrombosis',
                'finalEvaluation.thrombosis_type',
                'finalEvaluation.thrombosis_date',
                'finalEvaluation.has_allergic_reaction',
                'finalEvaluation.allergic_reaction_date',
                'finalEvaluation.allergic_reaction_seriousness',
                'finalEvaluation.allergic_reaction_comments',
                'finalEvaluation.has_adverse_reaction',
                'finalEvaluation.adverse_reaction',
            ]),
            fieldErrors() {
                let errors = [];
                errors['adverse-reaction']  = [this.$t('error.required')];
                return errors;
            }
        },
        methods: {
            validate() {
                this.$v.$touch();
                let isValid = !this.$v.$invalid;
                if (isValid) {
                    if (!this.exitus) {
                        this.exitus_date = null;
                        this.exitus_reason = '';
                    }
                    if (!this.has_thrombosis) {
                        this.thrombosis_type = '';
                        this.thrombosis_date = null;
                    }
                    if (!this.has_allergic_reaction) {
                        this.allergic_reaction_date = null;
                        this.allergic_reaction_seriousness = '';
                        this.allergic_reaction_comments = '';
                    }
                    if (!this.has_adverse_reaction) {
                        this.adverse_reaction = '';
                    }
                }
                return isValid;
            },
            back() {

            },
        },
        validations: {
            exitus_date: {
                dateMustBeValid: function () {
                    if (this.exitus) {
                        return this.$refs.exitusDate.invalidFeedback === '';
                    }
                    return true;
                }
            },
            thrombosis_date: {
                dateMustBeValid: function () {
                    if (this.has_thrombosis) {
                        return this.$refs.thrombosisDate.invalidFeedback === '';
                    }
                    return true;
                }
            },
            allergic_reaction_date: {
                dateMustBeValid: function () {
                    if (this.has_allergic_reaction) {
                        return this.$refs.allergicReactionDate.invalidFeedback === '';
                    }
                    return true;
                }
            },
            discharge_patient_date: {
                dateMustBeValid: function () {
                    return this.$refs.dischargePatientDate.invalidFeedback === '';
                }
            },
            adverse_reaction: {
                required: requiredIf(function () {
                     return this.has_adverse_reaction === true;
                })
            }
        }
    };
</script>

<style scoped>

</style>